/**
 * video-merge-music-plan.js — где на склейке звучит фоновая музыка.
 *
 * Чистая арифметика (ни DOM, ни WebAudio): по раскладке ленты и по тому, на
 * каких клипах человек оставил музыку, отвечает на два вопроса —
 *   • `start` — с какой секунды выхода звучит НАЧАЛО песни;
 *   • `env`   — огибающая громкости музыки [[t, g], …] в секундах выхода,
 *               кусочно-линейная, g ∈ [0, 1] (множитель к громкости музыки);
 *               null — музыка ровная от `start` до конца.
 *
 * ⚠️ ОДИН план на три места: движок (`VideoMergeWebCodecs` — opts.bgMusic.start
 * и .env), FFmpeg-граф (adelay + выражение `volume`) и превью (громкость
 * элемента на кадр). Каждое место зовёт план со СВОЕЙ раскладкой (у превью
 * переходов нет, у файла есть), поэтому стык музыки совпадает со стыком
 * картинки там, где его слышат.
 *
 * Правила (просьба с /video-merge 29.09.2026: «наложить музыку, а на
 * последнем видео оставить звук оригинальным»):
 *   • музыка звучит на подряд идущих клипах с музыкой — «участках»;
 *   • песня начинается с первого такого участка, а не с нуля выхода: если
 *     первый клип — вступление со своим звуком, песня не вступает с середины;
 *   • дальше песня идёт своим ходом: на клипах без музыки она затихает и
 *     возвращается позже по ходу песни — как дорожка музыки под лентой;
 *   • на клипе без музыки её нет совсем: съезд КОНЧАЕТСЯ до начала такого
 *     клипа и возврат НАЧИНАЕТСЯ после его конца (правило приглушения
 *     /video-editor, те же 0.35 с). Переход между клипами — сам окно съезда;
 *   • самое начало песни — без затухания (первая доля не должна «вплывать»),
 *     только 30 мс против щелчка; возврат посреди песни — плавный.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.VideoMergeMusicPlan = api;
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var RAMP = 0.35;   // с — съезд и возврат музыки на жёстком стыке (как у /video-editor)
    var EDGE = 0.03;   // с — начало песни на жёстком стыке: только против щелчка
    var EPS = 1e-6;

    /** Раскладка ленты: начала клипов по длительностям и перекрытиям переходов. */
    function layout(effDur, overlaps) {
        var d = (effDur || []).map(function (x) { return Math.max(0, +x || 0); });
        var n = d.length, ov = [], starts = [];
        for (var i = 0; i < n - 1; i++) ov.push(Math.max(0, +((overlaps || [])[i]) || 0));
        for (var k = 0; k < n; k++) starts.push(k ? starts[k - 1] + d[k - 1] - ov[k - 1] : 0);
        return { starts: starts, effDur: d, overlaps: ov, total: n ? starts[n - 1] + d[n - 1] : 0 };
    }

    function push(pts, t, g) {
        var last = pts[pts.length - 1];
        if (last && Math.abs(last[0] - t) < EPS && Math.abs(last[1] - g) < EPS) return;
        pts.push([t, g]);
    }

    /* Убрать точки внутри ровных отрезков: [a,1],[b,1],[c,1] → [a,1],[c,1]. */
    function simplify(pts) {
        var out = [];
        for (var i = 0; i < pts.length; i++) {
            var p = pts[i], prev = out[out.length - 1], next = pts[i + 1];
            if (prev && next && Math.abs(prev[1] - p[1]) < EPS && Math.abs(next[1] - p[1]) < EPS) continue;
            out.push(p);
        }
        return out;
    }

    /**
     * План музыки.
     * @param L   раскладка {starts[], effDur[], overlaps[], total}
     * @param on  on[k] — слышна ли музыка на клипе k
     * @param opts {ramp} — длина съезда на жёстком стыке, с
     * @return null — музыки нет ни на одном клипе; иначе
     *         {start, env, total, regions:[[a, b], …]}
     */
    function plan(L, on, opts) {
        opts = opts || {};
        var R = opts.ramp != null ? Math.max(0, +opts.ramp || 0) : RAMP;
        var n = (L && L.effDur) ? L.effDur.length : 0;
        if (!n) return null;
        var total = (L.total != null) ? +L.total : L.starts[n - 1] + L.effDur[n - 1];
        var raw = [];
        for (var k = 0; k < n;) {
            if (!on[k]) { k++; continue; }
            var m = k;
            while (m + 1 < n && on[m + 1]) m++;
            var a, rin, b, rout;
            if (k === 0) { a = 0; rin = 0; }
            else {
                var ovIn = +L.overlaps[k - 1] || 0;
                a = L.starts[k];                       // клип k начинается; окно перехода [a, a + ovIn]
                rin = ovIn > EPS ? ovIn : -1;          // -1: жёсткий стык, решится ниже (начало песни или возврат)
            }
            if (m === n - 1) { b = total; rout = 0; }
            else {
                var ovOut = +L.overlaps[m] || 0;
                var next = L.starts[m + 1];            // клип m+1 начинается; клип m кончается в next + ovOut
                if (ovOut > EPS) { b = next + ovOut; rout = ovOut; }
                else { b = next; rout = R; }
            }
            raw.push({ a: a, rin: rin, b: b, rout: rout });
            k = m + 1;
        }
        var regions = raw.filter(function (r) { return r.b - r.a > 0.01; });
        if (!regions.length) return null;

        // Участок как четыре точки: a — музыка начинает звучать, aEnd — звучит
        // полностью, bStart — начинает стихать, b — стихла.
        regions = regions.map(function (r, i) {
            var rin = r.rin < 0 ? (i === 0 ? EDGE : R) : r.rin;
            var rout = r.rout, len = r.b - r.a;
            if (rin + rout > len) {                     // короткий участок: съезды делят его пополам
                var f = len / (rin + rout);
                rin *= f; rout *= f;
            }
            return { a: r.a, aEnd: r.a + rin, bStart: r.b - rout, b: r.b };
        });
        // Короткий клип без музыки между двумя переходами: окна съезда и
        // возврата перекрываются (переход может быть длиннее половины клипа).
        // Музыка всё равно доходит до нуля — посередине между ними.
        for (var q = 1; q < regions.length; q++) {
            var pr = regions[q - 1], nx = regions[q];
            if (nx.a < pr.b) {
                var mid = (pr.b + nx.a) / 2;
                pr.b = mid; nx.a = mid;
                pr.bStart = Math.min(pr.bStart, mid);
                nx.aEnd = Math.max(nx.aEnd, mid);
            }
        }
        var pts = [];
        regions.forEach(function (r) {
            var aEnd = Math.min(Math.max(r.aEnd, r.a), r.b);
            var bStart = Math.max(Math.min(r.bStart, r.b), r.a);
            if (aEnd > bStart) aEnd = bStart = (aEnd + bStart) / 2;
            push(pts, r.a, aEnd - r.a > EPS ? 0 : 1);
            push(pts, aEnd, 1);
            push(pts, bStart, 1);
            push(pts, r.b, r.b - bStart > EPS ? 0 : 1);
        });
        // Время точек не убывает — огибающую читают линейным проходом (envAt
        // движка, gainAt здесь, выражение FFmpeg).
        for (var z = 1; z < pts.length; z++) if (pts[z][0] < pts[z - 1][0]) pts[z][0] = pts[z - 1][0];
        var env = simplify(pts);
        var flat = env.every(function (p) { return Math.abs(p[1] - 1) < EPS; });
        return {
            start: regions[0].a,
            env: flat ? null : env,
            total: total,
            regions: regions.map(function (r) { return [r.a, r.b]; }),
        };
    }

    /** Громкость огибающей в момент t (как envAt движка): до первой точки — её
     *  значение, после последней — её значение. Нет огибающей — 1. */
    function gainAt(env, t) {
        if (!env || !env.length) return 1;
        if (t <= env[0][0]) return env[0][1];
        for (var i = 1; i < env.length; i++) {
            if (t <= env[i][0]) {
                var t0 = env[i - 1][0], t1 = env[i][0], g0 = env[i - 1][1], g1 = env[i][1];
                return t1 <= t0 ? g1 : g0 + (g1 - g0) * ((t - t0) / (t1 - t0));
            }
        }
        return env[env.length - 1][1];
    }

    /** Какая секунда ПЕСНИ звучит в момент выхода t; null — песня ещё не
     *  началась или (без повтора) уже кончилась. */
    function songTime(p, t, dur, loop) {
        if (!p || !(dur > 0)) return null;
        var x = t - p.start;
        if (x < -EPS) return null;
        x = Math.max(0, x);
        if (loop) return x % dur;
        return x < dur ? x : null;
    }

    function num(x) {
        var s = (Math.round(x * 1000) / 1000).toFixed(3);
        s = s.replace(/0+$/, '').replace(/\.$/, '');
        return s === '-0' ? '0' : s;
    }

    /**
     * Та же огибающая выражением для FFmpeg `volume=eval=frame:volume='…'`
     * (t — секунда выхода). Кусочно-линейная: вложенные if(lt(t, …)).
     * Кавычки ставит вызывающий: внутри выражения есть запятые.
     */
    function ffmpegExpr(env) {
        if (!env || !env.length) return '1';
        var expr = num(env[env.length - 1][1]);
        for (var i = env.length - 1; i >= 1; i--) {
            var t0 = env[i - 1][0], t1 = env[i][0], g0 = env[i - 1][1], g1 = env[i][1];
            var seg;
            if (t1 - t0 < EPS || Math.abs(g1 - g0) < EPS) seg = num(t1 - t0 < EPS ? g1 : g0);
            else {
                var dg = g1 - g0;
                seg = num(g0) + (dg < 0 ? '-' : '+') + num(Math.abs(dg)) + '*(t-' + num(t0) + ')/' + num(t1 - t0);
            }
            expr = 'if(lt(t,' + num(t1) + '),' + seg + ',' + expr + ')';
        }
        return 'if(lt(t,' + num(env[0][0]) + '),' + num(env[0][1]) + ',' + expr + ')';
    }

    return {
        RAMP: RAMP,
        EDGE: EDGE,
        layout: layout,
        plan: plan,
        gainAt: gainAt,
        songTime: songTime,
        ffmpegExpr: ffmpegExpr,
    };
}));
